/**
 * Contact Exchange API Service
 * Contact exchange related API calls
 */

import { api, apiRaw } from '@/shared/api/apiClient';
import { API_ROUTES, buildUrl } from '@/config/apiRoutes';
import type { ContactExchange, ExchangeStatus } from '@/types/entities';
import type { AdminContactExchange, ContactExchangeStatus } from '@/types/admin';

// Re-export types for convenience
export type { ContactExchange, ExchangeStatus, AdminContactExchange, ContactExchangeStatus };

// ===========================================
// Helper Functions
// ===========================================

/**
 * Get next status in workflow
 */
const getNextStatus = (currentStatus: string): ContactExchangeStatus => {
    switch (currentStatus) {
        case 'pending':
            return 'contacted';
        case 'contacted':
            return 'completed';
        case 'completed':
            return 'pending';
        default:
            return 'pending';
    }
};

/**
 * Check if exchange is high priority (immediate timeline or high budget)
 */
const isHighPriority = (exchange: AdminContactExchange): boolean => {
    const isUrgent = exchange.timeline?.toLowerCase().includes('immediate') ||
        exchange.timeline?.toLowerCase().includes('asap') ||
        exchange.timeline?.toLowerCase().includes('within_30_days');
    const isHighBudget = (exchange.budget_max || 0) > 500000;
    return isUrgent || isHighBudget;
};

/**
 * Format timeline for display
 */
const formatTimeline = (timeline?: string): string => {
    if (!timeline) return 'Not specified';

    const timelineMap: Record<string, string> = {
        'immediate': 'Immediate',
        'within_30_days': 'Within 30 Days',
        'within_60_days': '1-2 Months',
        'within_90_days': '2-3 Months',
        'within_6_months': '3-6 Months',
        'within_year': '6-12 Months',
        'just_browsing': 'Just Browsing',
        'buying': 'Buying',
        'selling': 'Selling',
        'renting': 'Renting',
        'both': 'Buying & Selling',
    };

    return timelineMap[timeline.toLowerCase()] || timeline;
};

/**
 * Format budget range for display
 */
const formatBudgetRange = (min?: number, max?: number): string => {
    const formatCurrency = (n: number) => {
        if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
        if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
        return `$${n}`;
    };

    if (!min && !max) return 'Not specified';
    if (min && max) return `${formatCurrency(min)} - ${formatCurrency(max)}`;
    if (min) return `${formatCurrency(min)}+`;
    if (max) return `Up to ${formatCurrency(max)}`;
    return 'Not specified';
};

/**
 * Format date for display
 */
const formatDisplayDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// ===========================================
// API Response Types
// ===========================================

interface AdminContactExchangeResponse {
    success: boolean;
    data: {
        contacts: AdminContactExchange[];
        total: number;
    };
}

interface AdminContactExchangeUpdateResponse {
    success: boolean;
    data: AdminContactExchange;
}

// ===========================================
// Contact Exchange Service
// ===========================================
export const contactExchangeApi = {
    /**
     * Get admin contact exchanges (paginated)
     */
    getAdminContactExchanges: async (
        page: number = 1,
        perPage: number = 100,
        status?: string
    ): Promise<AdminContactExchangeResponse> => {
        const url = buildUrl(API_ROUTES.admin.contactExchanges.base, {
            page,
            per_page: perPage,
            ...(status && { status }),
        });
        return apiRaw.get<{ contacts: AdminContactExchange[]; total: number }>(url);
    },

    /**
     * Update admin contact exchange status
     */
    updateAdminContactExchangeStatus: async (
        id: number,
        status: ContactExchangeStatus
    ): Promise<AdminContactExchangeUpdateResponse> => {
        return apiRaw.post<AdminContactExchange>(
            API_ROUTES.admin.contactExchanges.updateStatus(id),
            { status }
        );
    },

    /**
     * Get all contact exchanges (simple list)
     */
    getExchanges: async (): Promise<ContactExchange[]> => {
        const response = await apiRaw.get<ContactExchange[]>(API_ROUTES.admin.contactExchanges.base);
        return response.data;
    },

    /**
     * Create contact exchange request
     */
    createExchange: async (targetId: number, message?: string): Promise<ContactExchange> => {
        const response = await apiRaw.post<ContactExchange>(
            API_ROUTES.admin.contactExchanges.create,
            { target_id: targetId, message }
        );
        return response.data;
    },

    /**
     * Soft-delete a batch of contact exchanges. Rows stay in the
     * DB and re-surface in /manage/bin.
     */
    bulkDelete: async (ids: number[]): Promise<{ deleted_count: number }> => {
        return api.post<{ deleted_count: number }>(
            '/contact-exchanges/bulk-delete',
            { ids },
        );
    },

    /** List soft-deleted contact exchanges (Bin view). */
    listDeleted: async (): Promise<{ contacts: AdminContactExchange[] }> => {
        return api.get('/contact-exchanges/deleted');
    },

    /** Clear deleted_at on a soft-deleted row. */
    restore: async (id: number): Promise<void> => {
        await api.post(`/contact-exchanges/${id}/restore`);
    },

    /** Hard-delete a soft-deleted row (irreversible). */
    purge: async (id: number): Promise<void> => {
        await api.delete(`/contact-exchanges/${id}/purge`);
    },

    // Helper functions
    getNextStatus,
    isHighPriority,
    formatTimeline,
    formatBudgetRange,
    formatDisplayDate,
};

export default contactExchangeApi;
